// RFC 6901 JSON Pointer — get / set / remove

function deepClone<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

function parseTokens(pointer: string): string[] {
	if (pointer === '' || pointer === '/') {
		return pointer === '/' ? [''] : [];
	}
	if (!pointer.startsWith('/')) {
		throw new Error(`Invalid JSON Pointer: "${pointer}" — must start with "/"`);
	}
	return pointer
		.slice(1)
		.split('/')
		.map((t) => t.replace(/~1/g, '/').replace(/~0/g, '~'));
}

export function jsonPointerGet(obj: unknown, pointer: string): unknown {
	const tokens = parseTokens(pointer);
	let current: unknown = obj;
	for (const token of tokens) {
		if (current === null || current === undefined) {
			return undefined;
		}
		if (Array.isArray(current)) {
			const idx = token === '-' ? current.length : Number(token);
			current = current[idx];
		} else if (typeof current === 'object') {
			current = (current as Record<string, unknown>)[token];
		} else {
			return undefined;
		}
	}
	return current;
}

export function jsonPointerSet(
	obj: Record<string, unknown>,
	pointer: string,
	value: unknown,
): Record<string, unknown> {
	if (pointer === '') {
		if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
			return value as Record<string, unknown>;
		}
		return obj;
	}
	const tokens = parseTokens(pointer);
	const result = deepClone(obj);
	let current: Record<string, unknown> | unknown[] = result;

	for (let i = 0; i < tokens.length - 1; i++) {
		const token = tokens[i];
		if (Array.isArray(current)) {
			const idx = Number(token);
			if (current[idx] === undefined || current[idx] === null || typeof current[idx] !== 'object') {
				current[idx] = {};
			}
			current = current[idx] as Record<string, unknown>;
		} else {
			const rec = current as Record<string, unknown>;
			if (rec[token] === undefined || rec[token] === null || typeof rec[token] !== 'object') {
				rec[token] = {};
			}
			current = rec[token] as Record<string, unknown>;
		}
	}

	const lastToken = tokens[tokens.length - 1];
	if (Array.isArray(current)) {
		const idx = lastToken === '-' ? current.length : Number(lastToken);
		current[idx] = value;
	} else {
		(current as Record<string, unknown>)[lastToken] = value;
	}

	return result;
}

export function jsonPointerRemove(
	obj: Record<string, unknown>,
	pointer: string,
): Record<string, unknown> {
	if (pointer === '') {
		return {};
	}
	const tokens = parseTokens(pointer);
	const result = deepClone(obj);
	let current: Record<string, unknown> | unknown[] = result;

	for (let i = 0; i < tokens.length - 1; i++) {
		const token = tokens[i];
		if (Array.isArray(current)) {
			current = current[Number(token)] as Record<string, unknown>;
		} else {
			current = (current as Record<string, unknown>)[token] as Record<string, unknown>;
		}
		if (current === undefined || current === null) {
			return result;
		}
	}

	const lastToken = tokens[tokens.length - 1];
	if (Array.isArray(current)) {
		current.splice(Number(lastToken), 1);
	} else {
		delete (current as Record<string, unknown>)[lastToken];
	}

	return result;
}

export function resolvePointerPath(basePath: string, path: string): string {
	if (path.startsWith('/')) {
		return path;
	}
	const base = basePath.endsWith('/') ? basePath : basePath + '/';
	return base + path;
}
